import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Camera, Image, XCircle } from 'lucide-react';
import { Form } from '@/firebase/formService';
import { formCompletionService } from '@/firebase/formCompletionService';
import { locationService, Location } from '@/firebase/locationService';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { fetchUserRoles, getUserSystemRoleForOrganization, isSystemUserInOrganization } from '@/utils/roleHelpers';
import { analyzeFormCompletion } from '@/services/geminiService';
import { uploadEvidenceFile } from '@/firebase/taskCompletionService';
import { complianceService } from '@/firebase/complianceService';
import { trackForm } from '@/utils/analytics';
import { evaluateFormula, type FormulaContext } from '@/utils/formulaEvaluator';
import { getUserPreferredLanguage, getFormName, getFieldLabel, getFormDescription } from '@/utils/formLanguageHelper';

interface RunFormProps {
  form: Form;
  assetId?: string; // Optional asset ID when running from asset page
  onClose: () => void;
  onComplete: () => void;
}

const RunForm: React.FC<RunFormProps> = ({ form, assetId, onClose, onComplete }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const preferredLanguage = getUserPreferredLanguage(user);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [runs, setRuns] = useState<any[]>([]);
  const [runsLocationFilterId, setRunsLocationFilterId] = useState<string>('ALL');
  const [selectedRun, setSelectedRun] = useState<any | null>(null);
  const [runSummaries, setRunSummaries] = useState<Record<string, string>>({});
  const [loadingSummaries, setLoadingSummaries] = useState<Record<string, boolean>>({});
  const [photoFields, setPhotoFields] = useState<Record<string, { file?: File; preview?: string }>>({});
  const [currentForm, setCurrentForm] = useState<Form>(form);
  const [isFormLoaded, setIsFormLoaded] = useState(false);

  // Fetch fresh form data when form.id changes to ensure we have latest locationIds
  useEffect(() => {
    const fetchFreshForm = async () => {
      if (form?.id) {
        try {
          const { formService } = await import('@/firebase/formService');
          const result = await formService.getForm(form.id);
          if (result.success && result.data) {
            setCurrentForm(result.data);
            setIsFormLoaded(true);
          } else {
            setCurrentForm(form);
            setIsFormLoaded(true);
          }
        } catch (error) {
          console.error('Error fetching fresh form:', error);
          setCurrentForm(form);
          setIsFormLoaded(true);
        }
      } else {
        setCurrentForm(form);
        setIsFormLoaded(true);
      }
    };
    
    setIsFormLoaded(false);
    fetchFreshForm();
  }, [form?.id]);

  const initializeForm = useCallback(() => {
    const vars: Record<string, string> = {};
    const values: Record<string, any> = {};

    // Initialize variables
    if (currentForm.variables) {
      currentForm.variables.forEach((variable) => {
        if (typeof variable === 'string') {
          vars[variable] = '';
        } else {
          vars[variable.name] = '';
        }
      });
    }

    // Initialize field values
    const photos: Record<string, { file?: File; preview?: string }> = {};
    currentForm.fields?.forEach((field) => {
      if (field.type === 'reference') {
        // Reference fields are not supported in standard forms (only in table forms)
        values[field.label] = '';
      } else if (field.type === 'calculated') {
        // Calculated fields will be computed by useEffect, initialize as empty for now
        values[field.label] = '';
      } else if (field.type === 'checkbox') {
        values[field.label] = false;
      } else if (field.type === 'radio') {
        values[field.label] = '';
      } else if (field.type === 'photo') {
        values[field.label] = '';
        photos[field.label] = {};
      } else {
        values[field.label] = field.defaultValue || '';
      }
    });

    setVariableValues(vars);
    setFieldValues(values);
    setPhotoFields(photos);
  }, [currentForm]);

  const loadHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      if (!currentForm.id) {
        setRuns([]);
        return;
      }
      const result = await formCompletionService.getCompletionsForForm(currentForm.id);
      if (result.success && result.data) {
        const allRuns = result.data.slice();
        allRuns.sort((a: any, b: any) => {
          const toMs = (d: any) => d?.toDate ? d.toDate().getTime() : (d instanceof Date ? d.getTime() : (typeof d === 'number' ? d : new Date(d).getTime()));
          return toMs(b.completionDate) - toMs(a.completionDate);
        });
        
        // Enrich runs with location names if missing or if locationName looks like an ID
        const enrichedRuns = await Promise.all(allRuns.map(async (run: any) => {
          // Check if locationName is missing or looks like an ID (long alphanumeric string)
          const locationNameLooksLikeId = run.locationName && 
            run.locationName.length > 15 && 
            /^[A-Za-z0-9]+$/.test(run.locationName) &&
            run.locationName === run.locationId;
          
          if ((!run.locationName || locationNameLooksLikeId) && run.locationId) {
            try {
              const locationResult = await locationService.getLocation(run.locationId);
              if (locationResult.success && locationResult.data) {
                run.locationName = locationResult.data.name;
              }
            } catch (error) {
              console.warn(`⚠️ Failed to fetch location name for run ${run.id}:`, error);
            }
          }
          return run;
        }));
        
        // Filter by location if a specific location is selected
        let filteredRuns = enrichedRuns;
        if (runsLocationFilterId && runsLocationFilterId !== 'ALL') {
          filteredRuns = enrichedRuns.filter((run: any) => run.locationId === runsLocationFilterId);
        }
        
        // Filter runs by accessible locations for system users
        if (isSystemUser && userLocationIds.length > 0) {
          filteredRuns = filteredRuns.filter((run: any) => 
            run.locationId && userLocationIds.includes(run.locationId)
          );
        }
        
        setRuns(filteredRuns);
        // Note: Summary generation will be triggered via useEffect when runs change
      } else {
        setRuns([]);
      }
    } catch (err) {
      console.error('Error loading form run history:', err);
      setRuns([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [currentForm.id, runsLocationFilterId, isSystemUser, userLocationIds]);

  const loadLocations = useCallback(async () => {
    try {
      let userLocationIds: string[] | undefined;
      let userRole: string | undefined;
      if (user?.id) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            userLocationIds = userData.locationIds;
            // Use organization-specific role if available
            if (currentOrganization?.id) {
              userRole = getUserSystemRoleForOrganization(userData, currentOrganization.id) || userData.role;
              setIsSystemUser(isSystemUserInOrganization(userData, currentOrganization.id));
            } else {
              userRole = userData.role;
              // Fallback: check if user is a system user using legacy method
              const roles = await fetchUserRoles(user.id);
              setIsSystemUser(roles.isSystemUser);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }

      const result = await locationService.getAccessibleLocations(
        currentOrganization?.id,
        user?.id,
        userRole,
        userLocationIds
      );

      if (result.success && result.data) {
        let validLocations = result.data.filter(loc => loc.id && loc.name);
        
        // If form has locationIds specified, filter to only show those locations
        const shouldFilter = currentForm?.locationIds && Array.isArray(currentForm.locationIds) && (currentForm.locationIds.length ?? 0) > 0;
        
        if (shouldFilter) {
          validLocations = validLocations.filter(loc => 
            loc.id && currentForm.locationIds!.includes(loc.id)
          );
        }
        
        setAvailableLocations(validLocations);
        
        // Store user location IDs
        setUserLocationIds(userLocationIds || []);
        
        // If not already set (i.e., no organization), check if user is a system user using legacy method
        if (user?.id && !currentOrganization?.id) {
          try {
            const roles = await fetchUserRoles(user.id);
            setIsSystemUser(roles.isSystemUser);
          } catch (error) {
            console.error('Error fetching user roles:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }, [currentForm, user, currentOrganization, isFormLoaded]);

  useEffect(() => {
    // Initialize form and track when component mounts or form changes
    initializeForm();
    // Track form started
    if (currentForm.id) {
      trackForm.started(currentForm.id);
    }
  }, [currentForm.id, initializeForm]);

  useEffect(() => {
    // Only call loadLocations after form is loaded to ensure we have fresh data
    if (isFormLoaded) {
      loadLocations();
    }
  }, [isFormLoaded, loadLocations]);

  useEffect(() => {
    // Auto-select location if user has access to only one location
    if (availableLocations.length === 1 && !selectedLocation) {
      setSelectedLocation(availableLocations[0]);
    }
  }, [availableLocations.length, selectedLocation]);

  useEffect(() => {
    // When location is selected, automatically filter past runs to that location
    if (selectedLocation?.id) {
      setRunsLocationFilterId(selectedLocation.id);
    }
  }, [selectedLocation?.id]);

  // Load history when form is loaded and when dependencies change
  // Use stringified userLocationIds to avoid reference equality issues that cause infinite loops
  const userLocationIdsString = useMemo(() => JSON.stringify([...userLocationIds].sort()), [userLocationIds]);
  
  useEffect(() => {
    // Only load history if form is loaded and we have a form ID
    // This prevents loading history before locations are loaded
    if (isFormLoaded && currentForm.id) {
      loadHistory();
    }
    // Note: We intentionally don't include loadHistory in deps to avoid loops
    // loadHistory is memoized with useCallback and will use latest values via closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentForm.id, runsLocationFilterId, isSystemUser, userLocationIdsString, isFormLoaded]);

  // Trigger summary generation when runs change
  useEffect(() => {
    if (runs.length > 0) {
      runs.forEach((run: any) => {
        if (run.id && !runSummaries[run.id] && !loadingSummaries[run.id]) {
          // Generate summary asynchronously (don't await)
          generateSummaryForRun(run);
        }
      });
    }
  }, [runs, runSummaries, loadingSummaries]);

  const handleFieldChange = (fieldLabel: string, value: any) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldLabel]: value
    }));
  };

  // Create a hash of non-calculated field values to use as dependency
  const nonCalculatedFieldValuesHash = useMemo(() => {
    if (!form.fields) return '';
    
    const nonCalculatedFields = form.fields.filter(f => f.type !== 'calculated');
    const values: Record<string, any> = {};
    
    nonCalculatedFields.forEach(field => {
      if (field.type !== 'reference') {
        values[field.label] = fieldValues[field.label];
      }
    });
    
    return JSON.stringify(values);
  }, [form.fields, fieldValues]);

  // Recalculate calculated fields whenever non-calculated field values change
  useEffect(() => {
    if (!form.fields) return;

    const calculatedFields = form.fields.filter(f => f.type === 'calculated' && f.formula);
    
    if (calculatedFields.length === 0) return;

    // Build formula context with current field values
    const context: FormulaContext = {
      fieldValues: { ...fieldValues },
      fields: form.fields
    };

    // Calculate values for all calculated fields
    const calculatedValues: Record<string, any> = {};
    calculatedFields.forEach(field => {
      if (field.formula) {
        try {
          const result = evaluateFormula(field.formula, context);
          calculatedValues[field.label] = result;
        } catch (error: any) {
          console.error(`Error calculating field "${field.label}":`, error);
          calculatedValues[field.label] = `Error: ${error.message}`;
        }
      }
    });

    // Only update calculated field values if they've changed
    setFieldValues(prev => {
      let hasChanges = false;
      const updated = { ...prev };
      
      Object.keys(calculatedValues).forEach(label => {
        if (updated[label] !== calculatedValues[label]) {
          updated[label] = calculatedValues[label];
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
  }, [nonCalculatedFieldValuesHash, form.fields, fieldValues]);

  const handlePhotoChange = (fieldLabel: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoFields(prev => ({
          ...prev,
          [fieldLabel]: {
            file,
            preview: reader.result as string
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (fieldLabel: string) => {
    setPhotoFields(prev => ({
      ...prev,
      [fieldLabel]: {}
    }));
    setFieldValues(prev => ({
      ...prev,
      [fieldLabel]: ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      showNotification('Please select a location', 'error');
      return;
    }

    // Validate required fields
    if (form.fields) {
      for (const field of form.fields) {
        // Skip validation for reference and calculated fields (they're auto-filled/computed)
        if (field.required && field.type !== 'reference' && field.type !== 'calculated') {
          if (field.type === 'photo') {
            const photo = photoFields[field.label];
            if (!photo || !photo.file) {
              showNotification(`Please upload a photo for required field: ${getFieldLabel(field, preferredLanguage)}`, 'error');
              setIsSubmitting(false);
              return;
            }
          } else {
            const value = fieldValues[field.label];
            if (value === undefined || value === null || value === '' || 
                (field.type === 'checkbox' && value === false)) {
              showNotification(`Please fill in the required field: ${getFieldLabel(field, preferredLanguage)}`, 'error');
              setIsSubmitting(false);
              return;
            }
          }
        }
      }
    }

    // Validate all variable values
    if (form.variables && form.variables.length > 0) {
      for (const variable of form.variables) {
        const varDef = typeof variable === 'string' 
          ? { name: variable as string, possibleValues: [] }
          : variable;
        
        if (!variableValues[varDef.name] || variableValues[varDef.name].trim() === '') {
          showNotification(`Please select a value for ${varDef.name}`, 'error');
          setIsSubmitting(false);
          return;
        }

        if (varDef.possibleValues && varDef.possibleValues.length > 0) {
          if (!varDef.possibleValues.includes(variableValues[varDef.name])) {
            showNotification(`${varDef.name} must be one of the predefined values`, 'error');
            setIsSubmitting(false);
            return;
          }
        }
      }
    }
    
    setIsSubmitting(true);
    
    try {
      // Upload photos first if any and update fieldValues with URLs
      const finalFieldValues: Record<string, any> = { ...fieldValues };
      
      // Reference fields are not supported in standard forms (only in table forms)

      // Include calculated field values (computed from formulas)
      for (const field of form.fields || []) {
        if (field.type === 'calculated' && fieldValues[field.label] !== undefined) {
          // Only include if calculation was successful (not an error string)
          const calculatedValue = fieldValues[field.label];
          if (calculatedValue !== undefined && calculatedValue !== null && 
              !(typeof calculatedValue === 'string' && calculatedValue.startsWith('Error:'))) {
            finalFieldValues[field.label] = calculatedValue;
          }
        }
      }
      
      // Upload photos and store URLs
      for (const field of form.fields || []) {
        if (field.type === 'photo' && photoFields[field.label]?.file) {
          try {
            const uploadResult = await uploadEvidenceFile(photoFields[field.label].file!, {
              docType: 'form_photo',
              organizationId: currentOrganization?.id,
              locationId: selectedLocation.id,
              formId: form.id,
              assetId,
              uploadedBy: user
                ? { id: user.id, email: user.email, name: user.name }
                : undefined,
              title: `${form.name} - ${field.label}`,
              evidenceLabel: field.label,
              source: 'web',
            });
            finalFieldValues[field.label] = uploadResult.url;
          } catch (error) {
            console.error(`Error uploading photo for ${field.label}:`, error);
            showNotification(`Failed to upload photo for ${field.label}`, 'error');
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Generate AI summary before saving
      let aiSummary: string | undefined;
      try {
        aiSummary = await analyzeFormCompletion(
          form.name,
          form.fields || [],
          finalFieldValues
        );
      } catch (summaryError: any) {
        console.error('Error generating AI summary:', summaryError);
        // Continue without summary if AI fails
        aiSummary = undefined;
      }

      // Fetch owner_role from the form's compliance template
      let ownerRole: string | undefined;
      if (form.templateId) {
        try {
          const templateResult = await complianceService.getTemplate(form.templateId);
          if (templateResult.success && templateResult.data) {
            ownerRole = templateResult.data.owner_role;
          }
        } catch (error) {
          console.warn('Error fetching compliance template for owner_role:', error);
          // Continue without owner_role if template fetch fails
        }
      }

      // Ensure aiSummary is always a string (never undefined or empty)
      const finalAiSummary = aiSummary && aiSummary.trim().length > 0 ? aiSummary.trim() : undefined;

      const result = await formCompletionService.addCompletion({
        formId: form.id || '',
        formName: form.name,
        locationId: selectedLocation.id!,
        locationName: selectedLocation.name,
        assetId: assetId, // Include assetId if provided
        variableValues: variableValues,
        fieldValues: finalFieldValues,
        aiSummary: finalAiSummary,
        completedBy: user ? `${user.name} (${user.email})` : 'Unknown',
        organizationId: currentOrganization?.id,
        owner_role: ownerRole,
      });

      if (result.success) {
        // Track form completion
        trackForm.completed(form.id || '');
        
        showNotification('Form submitted successfully!', 'success');
        await loadHistory();
        onComplete();
      } else {
        console.error('Failed to save form completion:', result.error);
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showNotification('Failed to submit form', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSummaryForRun = async (run: any) => {
    if (!run.id || runSummaries[run.id] || loadingSummaries[run.id]) {
      return; // Already have summary or currently loading
    }

    // If summary already exists in the run data (saved from submission), use it
    if (run.aiSummary) {
      setRunSummaries(prev => ({ ...prev, [run.id]: run.aiSummary }));
      return;
    }

    // Otherwise, generate it on the fly (for older runs that don't have summaries)
    // and save it to the database so we don't need to generate it again
    try {
      setLoadingSummaries(prev => ({ ...prev, [run.id]: true }));
      const summary = await analyzeFormCompletion(
        form.name,
        form.fields || [],
        run.fieldValues || {}
      );
      
      if (summary && summary.trim().length > 0) {
        // Save the generated summary to the database
        const updateResult = await formCompletionService.updateCompletion(run.id, {
          aiSummary: summary.trim()
        });
        
        if (updateResult.success) {
          // Update the run object with the new summary
          run.aiSummary = summary.trim();
        } else {
          console.error('Failed to save AI summary to database:', updateResult.error);
        }
        
        setRunSummaries(prev => ({ ...prev, [run.id]: summary.trim() }));
      } else {
        setRunSummaries(prev => ({ ...prev, [run.id]: 'Unable to generate summary.' }));
      }
    } catch (error) {
      console.error('Error generating summary for run:', error);
      setRunSummaries(prev => ({ ...prev, [run.id]: 'Unable to generate summary.' }));
    } finally {
      setLoadingSummaries(prev => ({ ...prev, [run.id]: false }));
    }
  };


  const formatDate = (date: any) => {
    try {
      let dateObj: Date;
      
      if (date?.toDate) {
        // Firestore Timestamp - convert to Date object
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'number') {
        dateObj = new Date(date);
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
      } else {
        dateObj = new Date(date);
      }
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Invalid Date';
      }
      
      // Format in user's local timezone
      return dateObj.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short', // This will show timezone (e.g., EST, PST)
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Filter runs - for system users, only show runs from accessible locations even when "ALL" is selected
  const filteredRuns = (() => {
    let filtered = runs;
    
    // Apply location filter dropdown selection
    if (runsLocationFilterId !== 'ALL') {
      filtered = filtered.filter((run: any) => run.locationId === runsLocationFilterId);
    }
    
    // For system users, ensure only accessible locations are shown even when "ALL" is selected
    if (isSystemUser && userLocationIds.length > 0) {
      filtered = filtered.filter((run: any) => 
        run.locationId && userLocationIds.includes(run.locationId)
      );
    }
    
    return filtered;
  })();

  const renderField = (field: any, index: number) => {
    const value = fieldValues[field.label] || '';

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={field.required}
            rows={3}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={field.required}
          >
            <option value="">Select...</option>
            {field.options?.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleFieldChange(field.label, e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              required={field.required}
            />
            <span className="text-sm text-gray-700">{field.placeholder || 'Check this box'}</span>
          </label>
        );

      case 'radio':
        return (
          <div className="space-y-1.5">
            {field.options?.map((option: string) => (
              <label key={option} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={field.label}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(field.label, e.target.value)}
                  className="w-4 h-4 border-gray-300 text-purple-600 focus:ring-purple-500"
                  required={field.required}
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={field.required}
          />
        );

      case 'reference':
        return (
          <div className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md">
            <span className="text-gray-700 font-medium">
              Reference fields are only supported in table forms
            </span>
          </div>
        );

      case 'calculated':
        const calculatedValue = fieldValues[field.label];
        const hasError = typeof calculatedValue === 'string' && calculatedValue.startsWith('Error:');
        return (
          <div className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md ${
            hasError ? 'bg-red-50 border-red-300' : 'bg-blue-50'
          }`}>
            <span className={hasError ? 'text-red-700' : 'text-blue-700 font-medium'}>
              {calculatedValue !== undefined && calculatedValue !== null ? String(calculatedValue) : 'Calculating...'}
            </span>
            <span className="ml-2 text-xs text-gray-500 italic">(Calculated)</span>
          </div>
        );

      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={field.required}
          />
        );

      case 'tel':
        return (
          <input
            type="tel"
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={field.required}
          />
        );

      case 'photo':
        const photo = photoFields[field.label];
        return (
          <div className="space-y-2">
            {photo?.preview ? (
              <div className="relative">
                <img
                  src={photo.preview}
                  alt={field.label}
                  className="w-full h-48 object-cover rounded border border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(field.label)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title="Remove photo"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Camera className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 10MB</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handlePhotoChange(field.label, e)}
                  className="hidden"
                  required={field.required && !photo?.file}
                />
              </label>
            )}
            {!photo?.preview && (
              <p className="text-xs text-gray-500 text-center">
                {field.required ? 'Required' : 'Optional'} photo field
              </p>
            )}
          </div>
        );

      default: // text
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            required={field.required}
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full flex flex-col" style={{ maxHeight: '95vh', height: '95vh' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div>
            <h3 className="text-xl font-semibold">{getFormName(currentForm, preferredLanguage)}</h3>
            {getFormDescription(form, preferredLanguage) && (
              <p className="text-sm text-purple-100 mt-1">{getFormDescription(form, preferredLanguage)}</p>
            )}
          </div>
          <button onClick={onClose} className="text-white hover:bg-purple-600 rounded-full p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content: Form and Past Runs */}
        <div className="flex-1 overflow-hidden p-4" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full" style={{ minHeight: 0 }}>
            {/* Left: Form */}
            <form onSubmit={handleSubmit} className="flex flex-col h-full border border-gray-200 rounded-lg" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
                {/* Location and Variable - Side by side */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Location <span className="text-red-600">*</span>
                    </label>
                    <select
                      value={selectedLocation?.id || ''}
                      onChange={(e) => {
                        const location = availableLocations.find(l => l.id === e.target.value);
                        setSelectedLocation(location || null);
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">Select a location...</option>
                      {availableLocations.map(location => (
                        <option key={location.id} value={location.id}>{location.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Variable Input Fields */}
                  {form.variables && form.variables.length > 0 && (
                    <div className="space-y-3">
                      {form.variables.map((variable, idx) => {
                        const varDef = typeof variable === 'string' 
                          ? { name: variable as string, possibleValues: [] }
                          : variable;
                        const hasValues = varDef.possibleValues && varDef.possibleValues.length > 0;
                        
                        return (
                          <div key={idx}>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {varDef.name} <span className="text-red-600">*</span>
                              {hasValues && (
                                <span className="text-xs text-gray-500 ml-1">
                                  ({varDef.possibleValues.length} option{varDef.possibleValues.length !== 1 ? 's' : ''})
                                </span>
                              )}
                            </label>
                            {hasValues ? (
                              <select
                                value={variableValues[varDef.name] || ''}
                                onChange={(e) => {
                                  setVariableValues(prev => ({
                                    ...prev,
                                    [varDef.name]: e.target.value
                                  }));
                                }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                              >
                                <option value="">Select {varDef.name.toLowerCase()}...</option>
                                {varDef.possibleValues.map((value) => (
                                  <option key={value} value={value}>{value}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={variableValues[varDef.name] || ''}
                                onChange={(e) => {
                                  setVariableValues(prev => ({
                                    ...prev,
                                    [varDef.name]: e.target.value
                                  }));
                                }}
                                placeholder={`Enter ${varDef.name.toLowerCase()}`}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Form Fields */}
                <div>
                  <div className="flex items-center justify-between mb-2 pb-2 border-b">
                    <h4 className="text-sm font-semibold text-gray-900">Form Fields</h4>
                    <span className="text-xs text-gray-500">
                      {form.fields?.length || 0} field{form.fields?.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {form.fields?.map((field, idx) => {
                      const displayLabel = getFieldLabel(field, preferredLanguage);
                      return (
                        <div key={idx} className="bg-white border border-gray-200 rounded-md p-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            {displayLabel}
                            {field.required && <span className="text-red-600 ml-1">*</span>}
                          </label>
                          {renderField(field, idx)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Form'}
                </button>
              </div>
            </form>

            {/* Right: History */}
            <div className="border border-gray-200 rounded-lg flex flex-col h-full" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <h4 className="text-sm font-semibold text-gray-900">Past Runs</h4>
                  <select
                    value={runsLocationFilterId}
                    onChange={(e) => {
                      const newFilterId = e.target.value;
                      setRunsLocationFilterId(newFilterId);
                      // Also update the location selection on the left side
                      if (newFilterId !== 'ALL') {
                        const location = availableLocations.find(loc => loc.id === newFilterId);
                        if (location) {
                          setSelectedLocation(location);
                        }
                      }
                    }}
                    className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  >
                  <option value="ALL">All Locations</option>
                  {availableLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1 overflow-y-auto p-3" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {isLoadingHistory ? (
                  <div className="text-center py-4 text-gray-500 text-xs">Loading...</div>
                ) : filteredRuns.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-xs">No past runs</div>
                ) : (
                  <div className="space-y-2">
                    {filteredRuns.map((run: any) => (
                      <div
                        key={run.id}
                        onClick={() => setSelectedRun(run)}
                        className={`p-2 border rounded cursor-pointer hover:bg-gray-50 text-xs ${
                          selectedRun?.id === run.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                        }`}
                      >
                        {(() => {
                          if (!run.id) return null;
                          // If run has a saved AI summary, use it directly
                          if (run.aiSummary) {
                            return <div className="font-medium text-gray-900 mb-1">{run.aiSummary}</div>;
                          }
                          // Otherwise check if we've generated one or are generating
                          if (loadingSummaries[run.id]) {
                            return <div className="text-gray-500 italic mb-1">Analyzing form data...</div>;
                          }
                          if (runSummaries[run.id]) {
                            return <div className="font-medium text-gray-900 mb-1">{runSummaries[run.id]}</div>;
                          }
                          // Trigger summary generation if not already loading
                          if (!loadingSummaries[run.id]) {
                            generateSummaryForRun(run);
                            return <div className="text-gray-500 italic mb-1">Loading summary...</div>;
                          }
                          return null;
                        })()}
                        <div className="text-gray-600 mt-0.5">{formatDate(run.completionDate)}</div>
                        {run.variableValues && Object.keys(run.variableValues).length > 0 && (
                          <div className="text-gray-500 mt-1 text-xs">
                            {Object.entries(run.variableValues).map(([key, value]) => (
                              <span key={key} className="mr-2">{key}: {value as string}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Selected Run Details Modal */}
        {selectedRun && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full flex flex-col" style={{ maxHeight: '80vh', height: '80vh' }}>
              <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                <h4 className="font-semibold text-gray-900">Run Details</h4>
                <button onClick={() => setSelectedRun(null)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <div>
                  <span className="font-medium text-gray-700">Location:</span>
                  <span className="ml-2 text-gray-900">{selectedRun.locationName}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Date:</span>
                  <span className="ml-2 text-gray-900">{formatDate(selectedRun.completionDate)}</span>
                </div>
                {(selectedRun.completedByName || selectedRun.completedBy) && (
                  <div>
                    <span className="font-medium text-gray-700">Completed By:</span>
                    <span className="ml-2 text-gray-900">{selectedRun.completedByName || selectedRun.completedBy}</span>
                  </div>
                )}
                {selectedRun.variableValues && Object.keys(selectedRun.variableValues).length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Variables:</span>
                    <div className="mt-1 space-y-1">
                      {Object.entries(selectedRun.variableValues).map(([key, value]) => (
                        <div key={key} className="ml-2 text-gray-900">
                          <span className="font-medium">{key}:</span> {value as string}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedRun.fieldValues && Object.keys(selectedRun.fieldValues).length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Form Data:</span>
                    <div className="mt-1 space-y-2">
                      {Object.entries(selectedRun.fieldValues).map(([key, value]) => {
                        // Helper to format date values in local time
                        const formatFieldValue = (val: any): string => {
                          if (val === null || val === undefined || val === '') {
                            return 'N/A';
                          }
                          
                          if (typeof val === 'boolean') {
                            return val ? 'Yes' : 'No';
                          }
                          
                          // Check if it's a date string (ISO format)
                          if (typeof val === 'string') {
                            const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;
                            const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
                            
                            if (isoDatePattern.test(val) || dateOnlyPattern.test(val)) {
                              try {
                                const dateObj = new Date(val);
                                if (!isNaN(dateObj.getTime())) {
                                  return dateObj.toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    timeZoneName: 'short',
                                  });
                                }
                              } catch (e) {
                                // Fall through to default string conversion
                              }
                            }
                          }
                          
                          // Check if it's a Date object
                          if (val instanceof Date) {
                            if (isNaN(val.getTime())) {
                              return 'Invalid Date';
                            }
                            return val.toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZoneName: 'short',
                            });
                          }
                          
                          // Check if it's a Firestore Timestamp
                          if (typeof val === 'object' && val !== null && val.toDate && typeof val.toDate === 'function') {
                            try {
                              const dateObj = val.toDate();
                              return dateObj.toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZoneName: 'short',
                              });
                            } catch (e) {
                              // Fall through to default string conversion
                            }
                          }
                          
                          return String(val);
                        };
                        
                        return (
                          <div key={key} className="ml-2 border border-gray-200 rounded p-2">
                            <span className="font-medium text-gray-700">{key}:</span>
                            <div className="mt-1 text-gray-900">
                              {formatFieldValue(value)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunForm;

